const { translateString } = window.BAO.Utils

/**
 * @typedef {{
 *   label: string;
 *   family: string;
 *   source: string;
 *   weight: string;
 * }} Font
 */

/**
 * @param {Font[]} fonts
 */
function loadFonts (fonts) {
  return Promise.all(
    fonts.map(font => {
      const fontFace = new FontFace(
        font.family,
        `url(${font.source}) format('woff2')`,
        { weight: font.weight },
      )

      return fontFace.load()
    }),
  )
}

class FoilingPreview extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['text']
  }

  /**
   * @param {{
   *  font: Font;
   *  text: string;
   * }} props
   */
  render ({ font, text }) {
    this.style.setProperty('--FoilingPreview_Font', font.family)
    this.style.setProperty('--FoilingPreview_FontWeight', font.weight)
    this.els.text.element.textContent = text
  }
}

if (!customElements.get('foiling-preview')) {
  customElements.define('foiling-preview', FoilingPreview)
}

class FoilingForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredElements () {
    return ['character-count', 'text-input']
  }

  connectedCallback () {
    super.connectedCallback()

    /**
     * @note This is recorded as soon as the element loads to reduce
     * the risk of a user manually overriding the limit
     */
    this.characterLimit = this.els['text-input'].element.maxLength

    /**
     * @note switching to timeout due to issues with Safari
     * and window.requestIdleCallback()
     */
    // window.requestIdleCallback(() => {
    //   this.loadFonts()
    // })

    setTimeout(() => {
      this.loadFonts()
    }, 1000)

    // Add a click event listener to the reset button
    document
      .querySelector('#clear-foiling')
      .addEventListener('click', event => {
        event.preventDefault()
        this.resetForm()
      })
  }

  resetForm () {
    // Reset the text input
    this.els['text-input'].element.value = ''
    this.els['text-input'].element.setAttribute('aria-invalid', false)

    // Reset the font inputs
    this.els['font-input'].elements.forEach((input, index) => {
      input.checked = index === 0
    })

    // Reset the character count
    this.els['character-count'].element.textContent = '0'
    this.els['character-count'].element.dataset.characterLimitReached = false

    // Reset the font label
    this.els['font-label'].element.textContent = ''

    // Reset the drawer toggles
    this.updateDrawerToggles()

    // Reset the previews
    this.updatePreviews()

    // Reset foiling choices
    document.querySelector('[data-product-form-el="foiling-text"]').value = ''
    document.querySelector('[data-product-form-el="foiling-font"]').value = ''

    // Reset Icons on foiling drawer trigger
    document
      .querySelector('.prd-DetailVariants_VariantDrawerToggle-check')
      .classList.add('prd-DetailVariants_VariantDrawerToggle-hidden')
    document
      .querySelector('.prd-DetailVariants_VariantDrawerToggle-edit')
      .classList.add('prd-DetailVariants_VariantDrawerToggle-hidden')
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.els['text-input'].element, 'input', event =>
      this.handleTextInput(event),
    )
    this.els['font-input'].elements.forEach(input => {
      this.listeners.add(input, 'input', event => this.handleFontChange(event))
    })
    this.listeners.add(this, 'input', () => this.updatePreviews())
    this.listeners.add(this, 'submit', event => this.handleSubmit(event))
  }

  /** @param {InputEvent} event */
  handleFontChange (event) {
    this.updateFontLabel(event.target.value)
  }

  /** @param {SubmitEvent} event */
  handleSubmit (event) {
    event.preventDefault()

    const hasInputError =
      this.els['text-input'].element.getAttribute('aria-invalid') === 'true'

    if (hasInputError) {
      return
    }

    this.applyFoiling()
    this.updateDrawerToggles()
    document.querySelector('site-drawers').close()
  }

  /** @param {InputEvent} event */
  handleTextInput (event) {
    this.validateTextInput(event)
    this.updateCharacterCount()
    this.updateSubmitState()
  }

  /** @param {InputEvent} event */
  validateTextInput (event) {
    /**
     * @param {number} limit
     * @returns {(string: string) => string}
     */
    function limitCharacterCount (limit) {
      return function (string) {
        return string.substring(0, limit)
      }
    }

    /**
     * @returns {(string: string) => string}
     */
    function toUpperCase (string) {
      return string.toUpperCase()
    }

    /**
     * Pass a series of functions to transform the string, then pass the string you wish to transform
     *
     * @example
     *   applyStringTransforms(
     *    removeCharacters(['!']),
     *    toUpperCase,
     *    limitCharacters(8),
     *  )('transform me!')
     *
     * @template {string}
     *
     * @param {...(value: string) => string} functions
     * @returns {(value: string) => string}
     */
    function applyStringTransforms (...functions) {
      return function (value) {
        return functions.reduce((accumulator, fn) => fn(accumulator), value)
      }
    }

    /**
     * @note Pass a function into `applyStringTransforms` for every change you wish to make
     * to the input value. `applyStringTransforms` will then apply those changes in order
     */
    event.target.value = applyStringTransforms(
      limitCharacterCount(this.characterLimit),
      toUpperCase,
    )(event.target.value)
  }

  updateCharacterCount () {
    const {
      'character-count': characterCount,
      'text-input': textInput,
    } = this.els

    const textLength = textInput.element.value.length

    characterCount.element.textContent = translateString(
      this.dataset.characterCountTemplate,
      {
        current: textLength,
        total: this.characterLimit,
      },
    )

    characterCount.element.dataset.characterLimitReached =
      textLength >= this.characterLimit
  }

  updateSubmitState () {
    this.externalEls.submit.element.disabled = false
  }

  /** @param {string} font */
  updateFontLabel (font) {
    this.els['font-label'].element.textContent = translateString(
      this.dataset.fontLabelTemplate,
      {
        font,
      },
    )
  }

  updatePreviews () {
    const formData = new FormData(this)

    const props = {
      font: this.selectedFont,
      text: formData.get('text'),
    }

    this.externalEls.preview.elements.forEach(preview => {
      preview.render(props)
    })
  }

  updateDrawerToggles () {
    const formData = new FormData(this)

    const text = formData.get('text')
    const { addFoilingTemplate, foilingAddedTemplate } = this.dataset
    const labelTemplate =
      text.length > 0 ? foilingAddedTemplate : addFoilingTemplate

    this.externalEls['toggle-label'].elements.forEach(toggleLabel => {
      toggleLabel.textContent = translateString(labelTemplate, {
        foiling: text,
      })
    })

    this.externalEls.toggle?.elements.forEach(toggle => {
      const checkIconContainer = toggle.querySelector(
        '.prd-DetailVariants_VariantDrawerToggle-check',
      )
      const editIconContainer = toggle.querySelector(
        '.prd-DetailVariants_VariantDrawerToggle-edit',
      )

      checkIconContainer.classList =
        'prd-DetailVariants_VariantDrawerToggle-check'
      editIconContainer.classList =
        'prd-DetailVariants_VariantDrawerToggle-edit'
    })
  }

  applyFoiling () {
    const formData = new FormData(this)

    const text = formData.get('text')
    const font = text.length > 0 ? formData.get('font') : ''

    this.dispatchEvent(
      new CustomEvent('product-foiling:set', {
        bubbles: true,
        detail: {
          font,
          text,
          formId: this.dataset.for,
        },
      }),
    )
  }

  loadFonts () {
    loadFonts(this.availableFonts).then(fonts => {
      fonts.forEach(font => {
        document.fonts.add(font)
      })
    })
  }

  /** @returns {Font[]} */
  get availableFonts () {
    return JSON.parse(this.dataset.fonts)
  }

  /** @returns {HTMLInputElement} */
  get selectedFontInput () {
    return this.els['font-input'].elements.find(element => element.checked)
  }

  get selectedFont () {
    return this.availableFonts.find(
      font => font.label === this.selectedFontInput.value,
    )
  }
}

if (!customElements.get('foiling-form')) {
  customElements.define('foiling-form', FoilingForm, { extends: 'form' })
}

class FoilingTextInput extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['input', 'error']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.input.element,
      'input',
      this.handleTextInput.bind(this),
    )
  }

  /** @param {InputEvent} event */
  handleTextInput (event) {
    this.validate(event.currentTarget.value)
  }

  /** @param {string} value */
  validate (value) {
    const failedTest = this.rules.find(rule => rule.test(value))
    const hasError = Boolean(failedTest)

    this.els.input.element.setAttribute('aria-invalid', hasError)
    this.els.error.element.setAttribute('aria-hidden', !hasError)
    this.els.error.element.textContent = failedTest?.errorMessage ?? ''
  }

  /**
   * @typedef {Object} InputValidationRule
   *
   * @property {string} id
   * @property {string} errorMessage
   * @property {(value: string) => boolean} test
   */

  /** @returns {InputValidationRule[]} */
  get rules () {
    /** @type {Record<string, string>} */
    const errorMessages = JSON.parse(this.dataset.errorMessages)

    return [
      {
        id: 'alphanumerical-fullstop-ampersand-only',
        test (value) {
          // Negated set. Matches any character that is not enclosed.
          const pattern = /[^a-zA-Z0-9&.]/g

          return pattern.test(value)
        },
      },
    ].map(rule => {
      return {
        ...rule,
        errorMessage: errorMessages[rule.id] || '',
      }
    })
  }
}

if (!customElements.get('foiling-text-input')) {
  customElements.define('foiling-text-input', FoilingTextInput)
}
